/**
 * Word Status Bar Component - Fluent UI Implementation
 * 
 * This component implements the Word status bar using Fluent UI v9 components.
 * All interactive elements have been replaced with appropriate Fluent UI components:
 * 
 * Component Mapping:
 * - Basic status items → Button (appearance="subtle", size="small")
 * - Text Predictions toggle → ToggleButton (appearance="subtle", size="small") 
 * - Icon-only controls → Button with icon prop (appearance="subtle", size="small")
 * - Zoom slider → Slider (size="small", preserved custom styling)
 * 
 * All Button and ToggleButton components use:
 * - appearance="subtle" for minimal visual weight
 * - size="small" for compact status bar sizing
 * - Custom hover state using Brand/Background/2/Hover design token
 * 
 * Design Token Usage:
 * - All colors use Fluent UI design tokens for consistency
 * - Word brand colors from tokens.ts for slider and accents
 * - Neutral color tokens for backgrounds, foregrounds, and strokes
 * 
 * Layout and visual parity with original implementation is maintained.
 */
import React, { useState } from 'react';
import {
  makeStyles,
  tokens,
  Slider,
  Button,
  ToggleButton,
} from '@fluentui/react-components';
import { typographyTokens } from '../../../tokens';

const useStyles = makeStyles({
  statusBar: {
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    width: '100%',
    height: '25px',
    backgroundColor: tokens.colorNeutralBackground3, // Neutral/Background/3/Rest
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`, // Neutral/Stroke/1/Rest
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0',
    ...typographyTokens.caption1Regular,
    color: tokens.colorNeutralForeground3, // Neutral/Foreground/3/Rest
    zIndex: 1000, // Ensure it stays on top
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
  },
  spacer: {
    flex: 1,
  },
  // Base styles for all Fluent UI components in status bar
  baseStatusBarComponent: {
    minWidth: '32px',
    height: '24px',
    border: 'none',
    backgroundColor: tokens.colorTransparentBackground,
    ...typographyTokens.caption1Regular,
    color: tokens.colorNeutralForeground3,
    '&:hover': {
      backgroundColor: `${tokens.colorBrandBackground2Hover} !important`,
    },
    // Target internal root elements
    '& .fui-Button__root, & .fui-ToggleButton__root': {
      minWidth: 'inherit',
      height: '24px',
      border: 'none',
      backgroundColor: tokens.colorTransparentBackground,
      ...typographyTokens.caption1Regular,
      color: tokens.colorNeutralForeground3,
      '&:hover': {
        backgroundColor: `${tokens.colorBrandBackground2Hover} !important`,
      },
    },
    // Global hover targeting
    '&:hover .fui-Button__root, &:hover .fui-ToggleButton__root': {
      backgroundColor: `${tokens.colorBrandBackground2Hover} !important`,
    },
  },
  // Text buttons (wider padding)
  statusBarButton: {
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`, // 4px 8px
    '& .fui-Button__root, & .fui-ToggleButton__root': {
      padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`, // 4px 8px
    },
  },
  // Icon-only buttons (square padding)
  statusBarIconButton: {
    padding: tokens.spacingVerticalXXS, // 4px
    '& .fui-Button__root': {
      padding: tokens.spacingVerticalXXS, // 4px
    },
  },
  // ToggleButton checked state
  statusBarToggleChecked: {
    '&[data-checked="true"]': {
      backgroundColor: `${tokens.colorNeutralBackground2} !important`,
      color: `${tokens.colorNeutralForeground2} !important`,
      '&:hover': {
        backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
      },
    },
    '& .fui-ToggleButton__root[data-checked="true"]': {
      backgroundColor: `${tokens.colorNeutralBackground2} !important`,
      color: `${tokens.colorNeutralForeground2} !important`,
      '&:hover': {
        backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
      },
    },
    '&[data-checked="true"]:hover .fui-ToggleButton__root': {
      backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
    },
  },
  // Target specific Fluent UI slider classes to force solid appearance
  solidSlider: {
    '& .fui-Slider__rail': {
      background: `${tokens.colorNeutralStroke1} !important`,
      border: 'none !important',
      outline: 'none !important',
    },
    '& .fui-Slider__track': {
      background: `${tokens.colorBrandBackground} !important`,
      border: 'none !important',
      outline: 'none !important',
    },
  },
});

interface WordStatusBarProps {
  characterCount?: number;
}

export const WordStatusBar: React.FC<WordStatusBarProps> = ({ characterCount = 0 }) => {
  const styles = useStyles();
  const [zoomLevel, setZoomLevel] = useState(15);
  const displayZoom = 100; // Keep display text at 100%

  return (
    <div className={styles.statusBar}>
      {/* Left Section */}
      <div className={styles.leftSection}>
        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton}`}
        >
          Page 1 of 1
        </Button>
        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton}`}
        >
          {characterCount} {characterCount === 1 ? 'character' : 'characters'}
        </Button>
        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton}`}
        >
          English (U.S.)
        </Button>
        <ToggleButton
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton} ${styles.statusBarToggleChecked}`}
          checked={true}
        >
          Text Predictions: On
        </ToggleButton>
      </div>

      {/* Spacer */}
      <div className={styles.spacer} />

      {/* Right Section */}
      <div className={styles.rightSection}>
        {/* Document Button */}
        <ToggleButton
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarIconButton} ${styles.statusBarToggleChecked}`}
          checked={true}
          icon={
            <img
              src="/src/assets/office/footer/Document.svg"
              alt="Document"
              style={{ width: '18px', height: '18px' }}
            />
          }
        />

        {/* Zoom Controls */}
        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarIconButton}`}
          icon={
            <img
              src="/src/assets/office/footer/CommonControlSliderMinus.svg"
              alt="Zoom Out"
              style={{ width: '12px', height: '12px' }}
            />
          }
        />

        <Slider
          size="small"
          value={zoomLevel}
          onChange={(_, data) => setZoomLevel(data.value)}
          className={styles.solidSlider}
          style={{ 
            width: '136px',
            margin: '0 4px',
            userSelect: 'none'
          }}
        />

        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarIconButton}`}
          icon={
            <img
              src="/src/assets/office/footer/CommonControlSliderPlus.svg"
              alt="Zoom In"
              style={{ width: '12px', height: '12px' }}
            />
          }
        />

        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton}`}
        >
          {displayZoom}%
        </Button>

        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton}`}
          icon={
            <img
              src="/src/assets/office/footer/AutoFit.svg"
              alt="Fit"
              style={{ width: '16px', height: '16px' }}
            />
          }
        >
          Fit
        </Button>

        <Button
          appearance="subtle"
          size="small"
          className={`${styles.baseStatusBarComponent} ${styles.statusBarButton}`}
        >
          Give Feedback to Microsoft
        </Button>
      </div>
    </div>
  );
};

export default WordStatusBar;
